import { shingleOptions } from "./materials"

export interface EstimateInput {
  roofSizeSqft: number
  complexity: "simple" | "normal" | "complex"
  selectedMaterial: string
  region: string
  hasSkylights: boolean
  skylightCount: number
  hasMultipleStories: boolean
  hasChimney: boolean
  hasAsbestos: boolean
  detachedGarageSize: "none" | "small" | "medium" | "large"
}

export interface LineItem {
  name: string
  category: string
  quantity: number
  unit: string
  unitPrice: number
  totalPrice: number
}

export interface EstimateResult {
  roofSizeSqft: number
  lineItems: LineItem[]
  roofingCost: number
  addonsCost: number
  serviceFee: number
  salesTax: number
  totalCost: number
  typicalContractorCost: number
  savings: number
}

// Waste factor increases effective sqft for complex roofs
function getWasteFactor(complexity: string): number {
  const factors: Record<string, number> = { simple: 1.10, normal: 1.15, complex: 1.20 }
  return factors[complexity] ?? 1.15
}

export function calculateEstimate(input: EstimateInput): EstimateResult {
  const { roofSizeSqft, complexity } = input
  const lineItems: LineItem[] = []
  const wasteFactor = getWasteFactor(complexity)

  // All-in base price: $4.50/sqft covers materials + labor + tear-off + disposal
  const basePrice = 4.50
  const effectiveSqft = roofSizeSqft * wasteFactor

  // Find selected shingle for display name
  const shingle = shingleOptions.find((s) => s.name === input.selectedMaterial) ?? shingleOptions[0]

  lineItems.push({
    name: `Complete Roof Replacement — ${shingle.name} (${shingle.brand})`,
    category: "roofing",
    quantity: effectiveSqft,
    unit: "sqft",
    unitPrice: basePrice,
    totalPrice: effectiveSqft * basePrice,
  })

  // Special features — these push the per-sqft cost toward $5.00+
  if (input.hasSkylights) {
    const count = input.skylightCount || 2
    lineItems.push({ name: "Skylight Flashing", category: "addon", quantity: count, unit: "each", unitPrice: 250, totalPrice: count * 250 })
  }
  if (input.hasChimney) {
    lineItems.push({ name: "Chimney Flashing", category: "addon", quantity: 1, unit: "each", unitPrice: 450, totalPrice: 450 })
  }
  if (input.hasMultipleStories) {
    lineItems.push({ name: "Multi-Story Access Surcharge", category: "addon", quantity: roofSizeSqft, unit: "sqft", unitPrice: 0.50, totalPrice: roofSizeSqft * 0.50 })
  }
  if (input.hasAsbestos) {
    lineItems.push({ name: "Asbestos Abatement", category: "addon", quantity: roofSizeSqft, unit: "sqft", unitPrice: 2.50, totalPrice: roofSizeSqft * 2.50 })
  }

  // Detached garage — same all-in rate
  if (input.detachedGarageSize !== "none") {
    const garageSizes = { small: 200, medium: 400, large: 700 } as const
    const garageSqft = garageSizes[input.detachedGarageSize]
    lineItems.push({
      name: `Detached Garage (${input.detachedGarageSize})`,
      category: "addon",
      quantity: garageSqft,
      unit: "sqft",
      unitPrice: basePrice,
      totalPrice: garageSqft * basePrice,
    })
  }

  // Calculate totals
  const roofingCost = lineItems
    .filter((item) => item.category === "roofing")
    .reduce((sum, item) => sum + item.totalPrice, 0)

  const addonsCost = lineItems
    .filter((item) => item.category === "addon")
    .reduce((sum, item) => sum + item.totalPrice, 0)

  const subtotal = roofingCost + addonsCost
  const serviceFee = subtotal * 0.10
  const salesTax = (subtotal + serviceFee) * 0.07
  const totalCost = subtotal + serviceFee + salesTax
  const typicalContractorCost = totalCost * 1.82
  const savings = typicalContractorCost - totalCost

  const round = (n: number) => Math.round(n * 100) / 100

  return {
    roofSizeSqft,
    lineItems,
    roofingCost: round(roofingCost),
    addonsCost: round(addonsCost),
    serviceFee: round(serviceFee),
    salesTax: round(salesTax),
    totalCost: round(totalCost),
    typicalContractorCost: round(typicalContractorCost),
    savings: round(savings),
  }
}
